<!--
  Share Log View Component Template
  
  Displays account share information in different view sizes:
  - skeletonOnly: Shows loading skeleton
  - large: Full detailed view with avatar, name, status, form name, and optional C-Form button
  - small: Compact view with avatar, name, status summary, and timestamp
  - self-collect: Special view for self-collection with status indicators and action buttons
-->

<!-- Skeleton Loading State: Shows animated placeholders while data is loading -->
@if (skeletonOnly) {
<div sxWidth="full" sxRow sxAlign="center" sxGap="10px" sxPadding="medium" sxBorderColor="one"
  sxBorderWidth="0 0 1px 0">
  <ion-skeleton-text sxCircle="50px" [animated]="true"></ion-skeleton-text>
  <div sxCol>
    <ion-skeleton-text sxWidth="100px" sxHeight="16px" [animated]="true"></ion-skeleton-text>
    <ion-skeleton-text sxWidth="50px" sxHeight="12px" [animated]="true"></ion-skeleton-text>
  </div>

</div>
}

<!-- Main Content: Renders when skeletonOnly is false -->
@if(!skeletonOnly){

<!-- Large View: Full detailed view with all share information -->
@if (!headerOnly && viewSize == 'large') {
<div sxBackground="light" sxBorderWidth="0 0 1px" sxCursor="pointer" sxBorderColor="one" sxPadding="small medium">
  <div sxRow sxAlign="center" sxDistribution="spaceBetween" sxGap="10px" (click)="openViewSharePage()">
    <!-- Avatar: Shows account display picture with lock indicator if quota is locked -->
    <avatar-round [lock]="accountShare?.isQuotaLocked" sxWidth="35px" [size]="30"
      [imageUrl]="accountToDisplay?.displayPictureUrl" [title]="accountToDisplay?.name"></avatar-round>

    <!-- Account Info: Name, timestamp, and optional parent account name -->
    <div sxCol sxAlign="start" sxDistribution="start" sxWidth="25%" class="min-w-0">
      <!-- shrink + truncate (no flex-grow): keeps "+ N" tight after the name; flex-1 would stretch short names -->
      <div sxRow sxGap="5px" sxAlign="center" sxWidth="100%" sxOverflow="hidden" class="min-w-0">
        <div class="flex min-w-0 w-full items-center gap-1">
          <span class="min-w-0 shrink truncate text-base m-0">{{ accountToDisplay?.name }}</span>
          @if (getParticipantsCount() > 1) {
          <span class="shrink-0 whitespace-nowrap text-base">+ {{ sharerCount - 1 }}</span>
          }
        </div>
      </div>
      <p sxTextSize="12px" sxColor="two" sxMargin="0" sxTextEllipse>
        {{ accountShare?.createTime | date : 'MMM d, h:mm a' }}
      </p>

      <!-- Account/branch name when Branches toggle is on -->
      @if (isShowParentAccountName) {
      <p sxTextSize="12px" sxColor="two" sxMargin="0" sxTextEllipse>
        {{ accountShare?.summary?.organisingParticipant?.name }}
      </p>
      }

    </div>

    <!-- Share Status: Icon and text indicating share status (Pending/Completed/Cancelled) -->
    <div sxWidth="15%" sxRow [sxColor]="getShareStatusColor(accountShare)" sxGap="2px" sxAlign="center"
      sxDistribution="start">
      <ion-icon [name]="getShareStatusIcon(accountShare)" sxTextSize="14px"></ion-icon>
      <p sxMargin="0" sxTextSize="12px">{{ getShareStatus(accountShare) }}</p>
    </div>

    <!-- Form Name: The name of the form/document being shared -->
    <p sxWidth="fill" sxTextSize="12px" sxColor="two" sxMargin="0" sxTextEllipse>
      {{ accountShare?.formName }}
    </p>

    <!-- C-Form Button: Shown only for foreign-related forms to copy transaction ID -->
    @if (isShowCFormButton(accountShare?.formName)) {
    <div sxHeight="fit" sxWidth="190px">
      <super-button type="outline" padding="10px" height="fit" sxWidth="190px" textSize="12px"
        (click)="copyTransactionId($event)">Copy C-Form code
      </super-button>
    </div>
    }
  </div>
</div>
}

<!-- Small View: Compact layout with essential information -->
@if (viewSize == 'small') {
<div sxBackground="light" sxBorderWidth="0 0 1px" sxCursor="pointer" sxBorderColor="one" sxRow sxGap="10px"
  sxAlign="center" sxPadding="medium" (click)="openViewSharePage()">

  <!-- Avatar: Larger size (50px) for small view -->
  <avatar-round [size]='50' [lock]="accountShare?.isQuotaLocked && ((activeAccountType$ | async) == 'organisation')"
    [imageUrl]='accountToDisplay?.displayPictureUrl' [title]='accountToDisplay?.name'></avatar-round>

  <!-- Account Name and Status Summary -->
  <div sxCol sxAlign="start" sxGap="5px" sxOverflow="hidden" class="min-w-0 flex-1">
    <h1 sxTextWeight="normal" sxMargin="0" sxTextSize="16px" sxTextEllipse sxWidth="full">{{accountToDisplay?.name}}</h1>
    <div sxRow sxAlign="center" sxGap="5px" sxWidth="full">
      <!-- Status Badge: Pending (open status) -->
      @if (accountShare?.status == ShareStatus.open) {
      <div class="text-sm" sxColor="warning-shade" sxRow sxAlign="center" sxGap="3px">
        <ion-icon name="alert-circle"></ion-icon>
        Pending |
      </div>
      }
      <!-- Status Badge: Cancelled -->
      @if (accountShare?.status == ShareStatus.cancelled) {
      <div class="text-sm" sxColor="danger" sxRow sxAlign="center" sxGap="3px">
        <ion-icon name="close-circle"></ion-icon>
        Cancelled |
      </div>
      }
      <!-- Share Summary: Context-specific summary text -->
      <p sxMaxWidth="fill" sxTextEllipse sxMargin="0" sxColor="two" sxTextSize="small" class='summary'>
        {{getShareSummary()}}</p>
    </div>
  </div>

  <!-- Timestamp: Formatted creation time -->
  <p sxTextSize="12px" sxColor="two" class="shrink-0 whitespace-nowrap">{{accountShare?.createTime | date : 'MMM d, h:mm a'}}</p>

</div>
}


<!-- Self-Collect View: Specialized view for self-collection scenarios with action buttons -->
@if (viewSize == 'self-collect') {
<div sxBackground="light" sxBorderWidth="0 0 1px" sxCursor="pointer" sxBorderColor="one" sxCol sxAlign="stretch">

  <!-- Header Section: Avatar, name, form name, and timestamp -->
  <div sxPadding="medium" sxRow sxAlign="center" sxDistribution="spaceBetween" sxGap="10px">
    <avatar-round [size]='50' [lock]="accountShare.isQuotaLocked && ((activeAccountType$ | async) == 'organisation')"
      [imageUrl]='accountToDisplay?.displayPictureUrl' [title]='accountToDisplay?.name'></avatar-round>

    <!-- Account Name and Form Name -->
    <div sxCol sxMaxWidth="fill" sxAlign="start" sxGap="5px" sxOverflow="hidden" class="min-w-0">
      <h1 sxTextWeight="normal" sxMargin="0" sxTextSize="16px" class="min-w-0 truncate">{{accountToDisplay?.name}}</h1>
      <p sxMargin="0" sxTextSize="small" sxColor="two">{{accountShare?.formName}}</p>
    </div>
    <p sxTextSize="12px" sxMargin="0 5px" sxColor="two">{{accountShare?.createTime | date : 'MMM d, h:mm a'}}</p>

  </div>

  <!-- Action Bar: Status indicator and action buttons -->
  <div sxRow sxAlign="stretch" sxDistribution="spaceBetween" sxBorderColor="one" sxBorderWidth="1px 0 0">
    <!-- Status Indicators: Visual status badges -->
    <div sxPadding="small medium" sxCenterize="row">
      @if (accountShare.status == ShareStatus.open) {
      <div sxTextSize="small" sxColor="warning" sxRow sxAlign="center" sxGap="5px">
        <ion-icon sxTextSize="medium" name="alert-circle"></ion-icon>
        Pending
      </div>
      }
      @if (accountShare.status == ShareStatus.completed) {
      <div sxTextSize="small" sxColor="success" sxRow sxAlign="center" sxGap="5px">
        <ion-icon sxTextSize="medium" name="checkmark-circle"></ion-icon>
        Completed
      </div>
      }
      @if (accountShare.status == ShareStatus.cancelled) {
      <div sxTextSize="small" sxColor="danger" sxRow sxAlign="center" sxGap="5px">
        <ion-icon sxTextSize="medium" name="remove-circle"></ion-icon>
        Cancelled
      </div>
      }
    </div>

    <!-- Action Buttons: Continue (for open shares) and View buttons -->
    <div sxRow sxGap="5px">
      <!-- Continue Button: Only shown for open/pending shares -->
      @if (accountShare.status == ShareStatus.open) {
      <super-button (click)="continueClicked.emit()" size="v-small" sxBorderColor="one" sxBorderWidth="0 1px"
        color="warning" iconEnd="sync-outline" background="light">Continue
      </super-button>
      }
      <!-- View Button: Opens detailed share view modal -->
      <super-button (click)="openViewSharePage()" size="v-small" background="light" iconEnd="chevron-forward-outline">
        View
      </super-button>
    </div>

  </div>

</div>
}
}